// Pulse — la portada por rol: hero del grupo + módulos de la dirección activa.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View , RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Header from '@/components/Header';
import HeroCarousel from '@/components/HeroCarousel';
import HoyCard from '@/components/HoyCard';
import PremioCard from '@/components/PremioCard';
import RoleSheet from '@/components/RoleSheet';
import StoriesModal from '@/components/StoriesModal';
import { WavesMark } from '@/components/Waves';
import { RoleModules } from '@/components/modules';
import { api, APP_VERSION, type Brief, type HotelRow, type Premio, type Source } from '@/api/client';
import { type Alerta } from '@/constants/data';
import { refrescarTodo, suscribir, valorActual } from '@/lib/refresco';
import { C, F, R } from '@/constants/theme';
import { useRole } from '@/context/RoleContext';
import { hoy, primerNombre, saludo } from '@/lib/format';
import { construirSlides } from '@/lib/stories';

export default function PulseScreen() {
  const [refrescando, setRefrescando] = useState(false);
  const insets = useSafeAreaInsets();
  const { role, puede, sesion } = useRole();
  const [brief, setBrief] = useState<Brief | null>(null);
  const [source, setSource] = useState<Source | undefined>(undefined);
  const [apiVer, setApiVer] = useState<string | null>(null);
  const [hoteles, setHoteles] = useState<HotelRow[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [premio, setPremio] = useState<Premio | null>(null);
  const [ageing, setAgeing] = useState<import('@/api/client').AgeingLive | null>(null);
  const [roleSheet, setRoleSheet] = useState(false);
  const [stories, setStories] = useState(false);

  /* Los datos de esta pantalla también escuchan el tirón: si no, se refrescaban
     las tarjetas de los módulos y el titular de arriba se quedaba viejo, que es
     peor que no refrescar nada. */
  const [tiron, setTiron] = useState(valorActual());
  useEffect(() => suscribir(setTiron), []);

  /* Se construyen UNA vez por render y se usan en dos sitios: el contador del
     botón y el propio pase. Calcularlas dos veces podía dar cuentas distintas. */
  const slides = construirSlides(brief, hoteles, alertas, premio, ageing);

  useEffect(() => {
    api.brief().then((r) => { setBrief(r.data); setSource(r.source); });
    api.serverVersion().then(setApiVer);
    // para las stories y la push con datos reales
    api.hoteles().then((r) => setHoteles(r.data));
    api.alertas().then((r) => setAlertas(r.data));
    api.premio().then((r) => setPremio(r.data));
    /* La cartera, SOLO para la diapositiva financiera de «Ayer en 10 segundos».
       Exige FINANCIERO en el servidor, así que si el rol no la tiene vuelve un
       fallo y la diapositiva simplemente no sale — no hay hueco ni error. */
    api.ageing().then((r) => setAgeing(r.ok ? r.dato : null)).catch(() => setAgeing(null));
    // 🔴 Aquí saltaba una push SIMULADA a los 2,5 s: resto del prototipo. En
    // producción parecía una notificación de verdad. Cuando existan las push
    // reales, el aviso volverá — disparado por el servidor, no por un temporizador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiron]);

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: insets.top }}>
      <Header onAvatar={() => setRoleSheet(true)} source={source} />
            {/* Tirar hacia abajo para refrescar. Faltaba lo obvio: hasta hoy la
          única forma de traer datos nuevos era cerrar la app y abrirla otra
          vez. Un tirón recarga TODAS las tarjetas a la vez, no solo esta
          pantalla — el contador de lib/refresco.ts lo escuchan todas. */}
      <ScrollView
        refreshControl={<RefreshControl refreshing={refrescando} onRefresh={() => {
          setRefrescando(true); refrescarTodo();
          setTimeout(() => setRefrescando(false), 900);
        }} tintColor={C.aqua} colors={[C.aqua]} progressBackgroundColor={C.bg2} />} contentContainerStyle={{ paddingHorizontal: 18, paddingBottom: 28 }} showsVerticalScrollIndicator={false}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <Text style={s.greet}>
            {saludo()}, <Text style={{ color: C.aqua }}>{primerNombre(sesion?.nombre) || role.short}</Text>
          </Text>
          <WavesMark width={24} />
        </View>
        <Text style={s.date}>{hoy()} · grupo Ona</Text>

        <HeroCarousel brief={brief} hoteles={hoteles} source={source} />

        <Pressable style={s.storiesBtn} onPress={() => setStories(true)}>
          <View style={s.playCircle}><Text style={{ color: C.aquaSoft, fontSize: 11 }}>▶</Text></View>
          <Text style={s.storiesTxt}>Ayer en 10 segundos</Text>
          {/* 🔴 El número, CONTADO. Decía «6 momentos» a mano, y las diapositivas se
              generan en runtime: entran o salen según haya alerta, premio o cartera.
              Al añadir la financiera ya eran siete y el botón seguía diciendo seis —
              un número escrito a mano al lado de una lista que cambia es un número
              que va a mentir tarde o temprano. */}
          <Text style={s.storiesSub}>{`${slides.length} momentos`}</Text>
        </Pressable>

        <View style={{ gap: 12, marginTop: 12 }}>
          <HoyCard />
          <RoleModules role={role.id} hoteles={hoteles} brief={brief} />
          {/* Los Premios Noa van AL FINAL (Patricio, 24-08). Estaban arriba, y
              arriba va lo que se mira todos los días: la producción, los
              hoteles, lo que requiere atención. El premio es de una vez al mes
              y se busca cuando se busca — no compite con el número del día. */}
          {/* A Dirección General solo cuando HAYA podio: hasta entonces la tarjeta
              ocupa sitio para decir que no hay nada, y lo que esa portada tiene
              que dar son números. En Sistemas se enseña siempre (ver modules). */}
          {role.id === 'dg' && puede('FINANCIERO') ? <PremioCard soloSiHayPodio /> : null}
        </View>

        <Text style={s.foot}>
          {source === 'live'
            ? 'Datos reales de Hotelos · lo marcado en ámbar aún no está conectado'
            : 'Beta Ona Pulse · datos simulados · fuente real: Hotelos'}
          {'\n'}app v{APP_VERSION} · api {apiVer ? `v${apiVer}` : 'sin conexión'}
        </Text>
      </ScrollView>

      <StoriesModal
        visible={stories}
        onClose={() => setStories(false)}
        slides={slides}
      />
      <RoleSheet visible={roleSheet} onClose={() => setRoleSheet(false)} />
    </View>
  );
}

const s = StyleSheet.create({
  greet: { fontFamily: F.displayMed, fontSize: 26, color: C.ink },
  date: { fontFamily: F.ui, fontSize: 12.5, color: C.muted, marginTop: 2, marginBottom: 12 },
  storiesBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 14,
    backgroundColor: C.aqua, borderRadius: R.pill, paddingVertical: 13, paddingHorizontal: 16,
    shadowColor: C.aqua, shadowOpacity: 0.28, shadowRadius: 20, shadowOffset: { width: 0, height: 6 }, elevation: 8,
  },
  playCircle: { width: 26, height: 26, borderRadius: 13, backgroundColor: '#06282E', alignItems: 'center', justifyContent: 'center' },
  storiesTxt: { fontFamily: F.uiSemi, fontSize: 14, color: '#06282E' },
  storiesSub: { marginLeft: 'auto', fontFamily: F.ui, fontSize: 11.5, color: '#06282E', opacity: 0.7 },
  foot: { fontFamily: F.ui, fontSize: 10, color: C.faint, textAlign: 'center', paddingVertical: 18 },
});
